import { FC, useEffect, useState } from "react";
import { Box, CircularProgress, Grid, Stack, Typography, Skeleton, Divider, Paper, Drawer } from '@mui/material';
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Utils } from "../../utils/common-utils";
import moment from 'moment';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import { SalaryNavigationBar } from '../../components/Salary/SalaryNavigationBar';
import { fetchSalaries, paySalary, payDailyWage, deleteSalary } from '../../store/salaryStore/salaryThunk';
import DialogShareSalarySlip from '../../components/DialogShareSalarySlip/DialogShareSalarySlip';

// Enhanced Salary View page with navigation and filters
const SalaryView: FC = () => {
  const { salaryId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { isLoggedIn, selectedAccount } = useAppSelector((state) => state.auth);
  const { salaries } = useAppSelector(state => state.salaries);

  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [salaryDetails, setSalaryDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSalaryId, setCurrentSalaryId] = useState(salaryId);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [selectedSalaryForShare, setSelectedSalaryForShare] = useState<any>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 20;

  // Update currentSalaryId when URL params change
  useEffect(() => {
    if (salaryId && salaryId !== currentSalaryId) {
      setCurrentSalaryId(salaryId);
      setIsLoading(true);
    }
  }, [salaryId]);

  // Fetch salaries in batches for navigation
  const fetchSalaryBatch = async (page: number = 1, append: boolean = false) => {
    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn) {
      if (append) setLoadingMore(true);

      try {
        const result: any = await dispatch(fetchSalaries({
          accountId,
        }));

        const newSalaries = result.payload || [];
        setHasMoreData(newSalaries.length === pageSize);

        console.log(`Fetched ${newSalaries.length} salaries for page ${page}`);
      } catch (error) {
        console.error('Error fetching salaries:', error);
        setHasMoreData(false);
      } finally {
        if (append) setLoadingMore(false);
      }
    }
  };

  // Initial load
  useEffect(() => {
    setCurrentPage(1);
    fetchSalaryBatch(1, false);
  }, [selectedAccount._id, isLoggedIn]);

  useEffect(() => {
    const fetchSalaryDetails = async () => {
      if (!currentSalaryId) return;

      try {
        setIsLoading(true);

        // Fetch salary details
        const response = await axios.get(`/api/v1/salary/${currentSalaryId}`);
        setSalaryDetails(response.data);

        // Generate PDF URL for preview
        const pdfResponse = await axios.get(`/api/v1/salary/${currentSalaryId}/download-slip`, {
          responseType: 'blob'
        });

        // Create blob URL for PDF preview
        const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);

      } catch (error) {
        console.error('Failed to fetch salary details:', error);
        toast.error('Failed to load salary details');
      } finally {
        setIsLoading(false);
      }
    };

    if (currentSalaryId) {
      fetchSalaryDetails();
    }

    // Cleanup blob URL on unmount
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [currentSalaryId]);

  const handleSalaryChange = (newSalaryId: string) => {
    if (newSalaryId !== currentSalaryId) {
      setCurrentSalaryId(newSalaryId);
      setIsLoading(true);
      setPdfUrl(''); // Clear old PDF

      // Update URL with new salary ID
      navigate(`/salary/${newSalaryId}/view`, {
        replace: true
      });
    }
  };

  // Handle loading more data when needed
  const handleLoadMore = () => {
    if (hasMoreData && !loadingMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchSalaryBatch(nextPage, true);
    }
  };

  // Handle direct salary search - search by TXN ID
  const handleDirectSalarySearch = async (txnId: string): Promise<boolean> => {
    if (!txnId.trim()) return false;

    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn) {
      try {
        // Search for salaries matching the TXN ID
        const foundSalaries = salaries.filter(sal =>
          sal.txnId && sal.txnId.toString().includes(txnId)
        );

        if (foundSalaries.length > 0) {
          console.log(`Found ${foundSalaries.length} salaries matching "${txnId}"`);

          // Load the first matching salary
          const firstSalary = foundSalaries[0];

          // Navigate to the first salary
          handleSalaryChange(firstSalary._id);

          return true;
        } else {
          alert(`No salaries found matching "${txnId}". Please try a different search.`);
          return false;
        }
      } catch (error) {
        console.error('Error searching salary:', error);
        alert('Error searching for salary. Please try again.');
        return false;
      }
    }
    return false;
  };

  const handleDownloadSlip = async (salId: string, employeeName: string) => {
    try {
      const response = await axios.get(`/api/v1/salary/${salId}/download-slip`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Salary_Slip_${employeeName.replace(/\s+/g, '_')}_${moment().format('YYYY_MM_DD')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Salary slip downloaded successfully');
    } catch (error) {
      console.error('Failed to download salary slip:', error);
      toast.error('Failed to download salary slip');
    }
  };

  const handleMarkAsPaid = async (salary: any) => {
    try {
      const paymentMode = salary.paymentMode || 'CASH';

      const paymentData: any = {
        id: salary._id,
        paymentDate: new Date(),
        paymentMode: paymentMode,
      };

      if (paymentMode !== 'CASH' && salary.fromBankAccount) {
        paymentData.fromBankAccount = typeof salary.fromBankAccount === 'string'
          ? salary.fromBankAccount
          : (salary.fromBankAccount as any)._id;
      }

      // Call appropriate API based on payment type
      if (salary.paymentType === 'DAILY_WAGE') {
        await dispatch(payDailyWage(paymentData)).unwrap();
      } else {
        await dispatch(paySalary(paymentData)).unwrap();
      }

      toast.success('Salary marked as paid successfully');
      // Refresh the details
      const response = await axios.get(`/api/v1/salary/${salary._id}`);
      setSalaryDetails(response.data);
      fetchSalaryBatch(1, false); // Refresh the list
    } catch (error) {
      console.error('Failed to mark as paid:', error);
      toast.error('Failed to mark as paid. Please try again.');
    }
  };

  const handleDelete = async (salId: string) => {
    if (window.confirm('Are you sure you want to delete this salary payment?')) {
      try {
        await dispatch(deleteSalary(salId)).unwrap();
        toast.success('Salary payment deleted successfully');
        navigate('/salary/list');
      } catch (error) {
        console.error('Failed to delete salary:', error);
        toast.error('Failed to delete salary payment');
      }
    }
  };

  const handleShare = (salary: any) => {
    setSelectedSalaryForShare(salary);
    setShareDrawerOpen(true);
  };

  const handleShareDrawerClose = () => {
    setShareDrawerOpen(false);
    setSelectedSalaryForShare(null);
  };

  return (
    <>
      <Box sx={{
        backgroundColor: '#fafafb',
        minHeight: '100vh',
        pt: 0,
        pb: 1
      }}>
        {/* Top Section with Navigation Bar and Quick Stats */}
        {isLoggedIn && (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 }, pb: 0.5 }}>
            <Grid container spacing={2}>
              {/* Navigation Bar and Actions */}
              <Grid item xs={12} md={12}>
                {(salaries && salaries.length > 0 && currentSalaryId) && (
                  <SalaryNavigationBar
                    currentSalaryId={currentSalaryId || ''}
                    onSalaryChange={handleSalaryChange}
                    salaries={salaries}
                    currentSalaryDetails={salaryDetails}
                    onLoadMore={handleLoadMore}
                    hasMoreData={hasMoreData}
                    loadingMore={loadingMore}
                    onDirectSearch={handleDirectSalarySearch}
                    onDownload={handleDownloadSlip}
                    onShare={handleShare}
                    onMarkAsPaid={handleMarkAsPaid}
                    onDelete={handleDelete}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <Grid container spacing={2} sx={{
          px: { xs: 1, sm: 2 },
          pt: 1
        }}>
          {/* Left side - PDF viewer */}
          <Grid item xs={12} md={8} sx={{
            order: { xs: 2, md: 1 }
          }}>
            {isLoading ? (
              <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                p: 3,
                height: { xs: '88vh', md: 'calc(100vh - 110px)' }
              }}>
                <Grid container>
                  <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <CircularProgress color="info" />
                    <Typography sx={{ mt: 2 }}>Loading Salary Slip...</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton width="100%" height={300} />
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                height: { xs: '88vh', md: 'calc(100vh - 110px)' }
              }}>
                <object
                  style={{ height: "100%", width: "100%" }}
                  data={pdfUrl}
                  type="application/pdf"
                />
              </Box>
            )}
          </Grid>

          {/* Right side - Salary Details */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>

            {/* Quick Stats */}
            <Grid item xs={12} md={12} sx={{ pb: 2 }}>
              {salaries && salaries.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    m: 0,
                    border: '1px solid #e9ecf1',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                    {/* Total Salaries */}
                    <Box sx={{
                      flex: 1,
                      textAlign: 'center',
                      borderRight: '1px solid #e9ecf1',
                      pr: 1
                    }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          mb: 0.25,
                          lineHeight: 1.2
                        }}
                      >
                        {salaries.length}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        Total
                      </Typography>
                    </Box>

                    {/* Pending Salaries */}
                    <Box sx={{
                      flex: 1,
                      textAlign: 'center',
                      borderRight: '1px solid #e9ecf1',
                      px: 1
                    }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'warning.main',
                          mb: 0.25,
                          lineHeight: 1.2
                        }}
                      >
                        {salaries.filter(sal => sal.paymentStatus !== 'PAID').length}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        Pending
                      </Typography>
                    </Box>

                    {/* Paid Salaries */}
                    <Box sx={{
                      flex: 1,
                      textAlign: 'center',
                      pl: 1
                    }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'success.main',
                          mb: 0.25,
                          lineHeight: 1.2
                        }}
                      >
                        {salaries.filter(sal => sal.paymentStatus === 'PAID').length}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        Paid
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Grid>

            
            {/* Monthly Salary Slip Details Section */}
            <Box sx={{
              borderRadius: '12px',
              border: '1px solid #e9ecf1',
              backgroundColor: '#fff',
              display: { xs: 'none', sm: 'block' }
            }}>
              <Box sx={{
                bgcolor: '#f5f5f5',
                p: { xs: 1.5, sm: 2 },
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  {salaryDetails?.data?.paymentType === 'MONTHLY_SALARY' ? 'Monthly Salary' : 'Daily Wage'} Slip
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                >
                  {salaryDetails?.data?.createdAt
                    ? moment(salaryDetails.data.createdAt).format('DD MMM YYYY')
                    : 'N/A'} • TXN #{salaryDetails?.data?.txnId} • {salaryDetails?.data?.paymentStatus}
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={{ xs: 1.5, sm: 2 }}>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Employee Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {salaryDetails?.data?.employee?.name || 'N/A'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Net Payable Amount
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                      }}
                    >
                      {salaryDetails?.data?.netSalary ? Utils.formatNumber(parseFloat(salaryDetails.data.netSalary).toFixed(2), true, true) : '₹0'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Period
                    </Typography>
                    <Typography variant="body1">
                      {salaryDetails?.data?.paymentType === 'MONTHLY_SALARY'
                        ? salaryDetails?.data?.paymentPeriod?.month || 'N/A'
                        : salaryDetails?.data?.paymentPeriod?.fromDate && salaryDetails?.data?.paymentPeriod?.toDate
                          ? `${moment(salaryDetails.data.paymentPeriod.fromDate).format('DD/MM/YYYY')} - ${moment(salaryDetails.data.paymentPeriod.toDate).format('DD/MM/YYYY')}`
                          : 'N/A'
                      }
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: salaryDetails?.data?.paymentStatus === 'PAID'
                          ? 'success.main'
                          : 'warning.main'
                      }}
                    >
                      {salaryDetails?.data?.paymentStatus || 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Type
                    </Typography>
                    <Typography variant="body1">
                      {salaryDetails?.data?.paymentType === 'MONTHLY_SALARY' ? 'Monthly Salary' : 'Daily Wage'}
                    </Typography>
                  </Box>

                </Stack>
              </Box>
            </Box>

            {/* Salary Breakdown Section */}
            {!isLoading && salaryDetails?.data && (
              <Box sx={{
                borderRadius: '12px',
                border: '1px solid #e9ecf1',
                backgroundColor: '#fff',
                mt: 2,
                mb: 2,
                overflowY: 'auto'
              }}>
                <Box sx={{
                  bgcolor: '#f5f5f5',
                  p: 2,
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    Salary Breakdown
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    {/* Earnings Section */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, bgcolor: '#f0f9ff', p: 1, borderRadius: 1 }}>
                        Earnings
                      </Typography>
                      <Stack spacing={1}>
                        {salaryDetails.data.earnings?.basic > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Basic Salary:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.earnings.basic).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.earnings?.hra > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">HRA:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.earnings.hra).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.earnings?.additionalEarnings?.map((earning: any, idx: number) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{earning.label}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(earning.amount).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        ))}
                        {salaryDetails.data.earnings?.allowances?.map((allowance: any, idx: number) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{allowance.headName}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(allowance.amount).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        ))}
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Total Earnings:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>
                            {Utils.formatNumber(parseFloat(salaryDetails.data.earnings?.totalEarnings || 0).toFixed(2), true, true)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Deductions Section */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, bgcolor: '#fef2f2', p: 1, borderRadius: 1 }}>
                        Deductions
                      </Typography>
                      <Stack spacing={1}>
                        {salaryDetails.data.deductions?.pf > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">PF:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.pf).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.esi > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">ESI:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.esi).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.tds > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">TDS:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.tds).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.professionalTax > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Professional Tax:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.professionalTax).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.advance > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Advance:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.advance).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.loan > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Loan:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.loan).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.otherDeductions?.map((deduction: any, idx: number) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{deduction.headName}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(deduction.amount).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        ))}
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Total Deductions:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                            {Utils.formatNumber(parseFloat(salaryDetails.data.deductions?.totalDeductions || 0).toFixed(2), true, true)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Net Payable */}
                    <Box sx={{ bgcolor: '#ecfdf5', p: 1.5, borderRadius: 1, border: '1px solid #a7f3d0' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#065f46' }}>Net Payable:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#059669' }}>
                          {Utils.formatNumber(parseFloat(salaryDetails.data.netSalary || 0).toFixed(2), true, true)}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            )}

          </Grid>
        </Grid>
      </Box>

      {/* Share Salary Slip Drawer */}
      <Drawer
        anchor="right"
        open={shareDrawerOpen}
        onClose={handleShareDrawerClose}
      >
        {selectedSalaryForShare?.data && (
          <DialogShareSalarySlip
            salaryId={selectedSalaryForShare.data._id}
            employeeName={selectedSalaryForShare.data.employee?.name || ''}
            employeeEmail={selectedSalaryForShare.data.employee?.personalEmail || ''}
            employeePhone={selectedSalaryForShare.data.employee?.primaryPhone || ''}
            openDrawer={shareDrawerOpen}
            onClose={handleShareDrawerClose}
          />
        )}
      </Drawer>
    </>
  );
};

export default SalaryView;
